import { deleteAccount } from '../auth/api.js';
import { decodeJwtPayload } from '../auth/jwt.js';
import { clearSession, getAccessToken, getUserEmail } from '../auth/storage.js';
import { clearAccountData } from '../auth/session.js';
import { goBack, navigate } from '../router.js';
import { offloadUserData } from '../study/userData.js';
import { confirmDialog, setLoading } from './feedback.js';
import { escapeHtml } from '../utils/html.js';
import { logoMark } from './branding.js';
import { ICONS, logoSvg } from './icons.js';

/** Navigable menu entries (order matches the product spec). */
const NAV_ITEMS = [
  { key: 'favorites', label: 'Ulubione lekcje', href: '/favorites', icon: ICONS.heart('h-5 w-5') },
  { key: 'phrases', label: 'Słownik', href: '/phrases', icon: ICONS.phrases() },
  { key: 'how-to-learn', label: 'Jak się uczyć?', href: '/jak-sie-uczyc', icon: ICONS.bulb() },
  { key: 'about', label: 'O aplikacji', href: '/o-aplikacji', icon: ICONS.about() },
];

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** The signed-in e-mail: stored value first, JWT claim as a fallback. */
export function currentUserEmail() {
  return getUserEmail() || decodeJwtPayload(getAccessToken())?.email || '';
}

function navRow({ key, label, href, icon: rowIcon }, active) {
  const isActive = key === active;
  return `
    <a href="#${href}" data-nav
      class="flex items-center gap-3.5 px-4 py-3.5 text-base font-medium text-neutral-900 transition active:bg-neutral-100 ${isActive ? 'bg-neutral-100/70' : ''}">
      <span class="text-neutral-400">${rowIcon}</span>
      <span class="flex-1">${label}</span>
      <span class="text-neutral-300">${ICONS.chevronRight('h-4 w-4')}</span>
    </a>`;
}

/* -------------------------------------------------------------------------- */
/* Shell                                                                      */
/* -------------------------------------------------------------------------- */

/** Default leading slot: the brand mark, linked home. */
function defaultLeading() {
  return `
    <a href="#/" aria-label="Strona główna" class="flex items-center">
      ${logoMark()}
    </a>`;
}

/**
 * Render an authenticated screen: a minimalist, borderless top bar with an
 * iOS-style hamburger drawer, plus the caller's content.
 *
 * @param {HTMLElement} mount
 * @param {{ active?: string, contentHtml?: string, leading?: string,
 *           onMount?: (main: HTMLElement) => void }} options
 *   - active: key of the current NAV_ITEM (highlighted in the drawer).
 *   - contentHtml: markup for the main content region.
 *   - leading: markup for the top bar's leading (left) slot. Each view supplies
 *     its own — e.g. `logoMark()` on home, `backButton()` on a sub-screen.
 *     Omit to fall back to the home-linked brand mark.
 *   - onMount: called with the <main> element once it's in the DOM.
 */
export function renderAppScreen(mount, { active = '', contentHtml = '', leading, onMount } = {}) {
  const email = currentUserEmail();

  // Defensive: a previous screen may have left the scroll lock on <html>.
  document.documentElement.classList.remove('overflow-hidden');

  mount.className = 'flex min-h-dvh flex-col';
  mount.innerHTML = `
    <header class="sticky top-0 z-30 bg-white/80 backdrop-blur">
      <div class="mx-auto flex w-full max-w-sm items-center justify-between px-4 py-3.5">
        ${leading ?? defaultLeading()}
        <button id="menu-open" type="button" aria-label="Otwórz menu" aria-controls="app-menu" aria-expanded="false"
          class="grid h-10 w-10 place-items-center rounded-xl text-neutral-700 transition active:scale-95 active:bg-neutral-100">
          ${ICONS.hamburger()}
        </button>
      </div>
    </header>

    <main id="screen-content" class="mx-auto flex w-full max-w-sm flex-1 flex-col px-4 pb-10">
      ${contentHtml}
    </main>

    <!-- iOS-style slide-in drawer -->
    <div id="app-menu" data-state="closed" inert
      class="group fixed inset-0 z-50 pointer-events-none data-[state=open]:pointer-events-auto">
      <div id="menu-backdrop"
        class="absolute inset-0 bg-neutral-900/40 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-data-[state=open]:opacity-100"></div>

      <aside role="dialog" aria-modal="true" aria-label="Menu"
        class="absolute inset-y-0 right-0 flex w-80 max-w-[85%] translate-x-full flex-col bg-white shadow-soft-lg transition-transform duration-300 ease-out group-data-[state=open]:translate-x-0"
        style="padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom);">
        <div class="flex items-center justify-between px-5 pb-1 pt-4">
        <div>
              ${logoSvg(32)}
        </div>
          <button id="menu-close" type="button" aria-label="Zamknij menu"
            class="grid h-9 w-9 place-items-center rounded-xl text-neutral-500 transition active:scale-95 active:bg-neutral-100">
            ${ICONS.close()}
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-4 pb-4">
          <!-- User element -->
          <div class="mt-2 flex items-center gap-3 rounded-2xl bg-neutral-50 px-4 py-3.5">
            <div class="min-w-0">
              <p class="text-xs text-neutral-500">Witaj</p>
              <p id="menu-email" class="truncate text-sm font-semibold text-neutral-900">${escapeHtml(email || '—')}</p>
            </div>
          </div>

          <!-- Navigation group -->
          <nav class="mt-5 overflow-hidden rounded-2xl bg-neutral-50 divide-y divide-neutral-200/70">
            ${NAV_ITEMS.map((item) => navRow(item, active)).join('')}
          </nav>

          <!-- Destructive group -->
          <div class="mt-5 overflow-hidden rounded-2xl bg-neutral-50">
            <button id="menu-delete" type="button"
              class="flex w-full items-center gap-3.5 px-4 py-3.5 text-left text-base font-medium text-red-600 transition active:bg-red-50">
              <span class="text-red-400">${ICONS.trash()}</span>
              <span class="flex-1">Usuń konto</span>
            </button>
          </div>
        </div>

        <!-- Logout pinned at the bottom -->
        <div class="px-4 pb-5 pt-1">
          <button id="menu-logout" type="button"
            class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-700 px-2 py-3 text-sm font-semibold text-white transition hover:bg-neutral-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-neutral-900/20 active:scale-[0.99]">
            ${ICONS.logout()}
            <!-- submit-label is the hook setLoading swaps while the backup uploads. -->
            <span class="submit-label">Wyloguj</span>
          </button>
        </div>
      </aside>
    </div>`;

  wireMenu(mount);
  wireBack(mount);

  const main = mount.querySelector('#screen-content');
  if (typeof onMount === 'function') onMount(main);
}

/* -------------------------------------------------------------------------- */
/* Back affordance                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Route the leading slot's back link (`backButton()`) through the history stack
 * instead of its `href`, so the previous screen returns exactly as it was left —
 * same route, same scroll position. The `href` stays as the fallback for a cold
 * start with no app entry behind us, and for opening the link in a new tab.
 *
 * Exported because the legal screens (`views/legal.js`) render without the shell
 * when nobody is signed in, and their back affordance must still behave like
 * every other one — one definition, both chromes.
 */
export function wireBack(mount) {
  const back = mount.querySelector('[data-back]');
  if (!back) return;
  back.addEventListener('click', (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey) return; // new tab / window
    event.preventDefault();
    goBack(back.getAttribute('href')?.replace(/^#/, '') || '/');
  });
}

/* -------------------------------------------------------------------------- */
/* Drawer behaviour                                                           */
/* -------------------------------------------------------------------------- */

function wireMenu(mount) {
  const menu = mount.querySelector('#app-menu');
  const openBtn = mount.querySelector('#menu-open');
  const closeBtn = mount.querySelector('#menu-close');
  const root = document.documentElement;
  // Everything behind the drawer — made inert while it's open (focus trap).
  const background = Array.from(mount.children).filter((el) => el !== menu);

  const isOpen = () => menu.dataset.state === 'open';

  const onKey = (event) => {
    if (event.key === 'Escape') closeMenu();
  };

  function openMenu() {
    menu.dataset.state = 'open';
    menu.removeAttribute('inert');
    background.forEach((el) => el.setAttribute('inert', ''));
    openBtn.setAttribute('aria-expanded', 'true');
    root.classList.add('overflow-hidden');
    document.addEventListener('keydown', onKey);
    closeBtn.focus();
  }

  function closeMenu() {
    if (!isOpen()) return;
    menu.dataset.state = 'closed';
    menu.setAttribute('inert', '');
    background.forEach((el) => el.removeAttribute('inert'));
    openBtn.setAttribute('aria-expanded', 'false');
    releaseGlobals();
    openBtn.focus();
  }

  /**
   * Undo the two things an open drawer does *outside* its own subtree: the scroll
   * lock on `<html>` and the document-level Escape listener. Split out of
   * `closeMenu` because a navigation can take the drawer's DOM away without any
   * of its close affordances ever firing — see the `hashchange` handler below.
   */
  function releaseGlobals() {
    root.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', onKey);
  }

  openBtn.addEventListener('click', openMenu);
  closeBtn.addEventListener('click', closeMenu);
  mount.querySelector('#menu-backdrop').addEventListener('click', closeMenu);

  // Leaving the screen with the drawer still open (system back button, `goBack()`,
  // a hand-edited hash — anything that isn't one of the affordances above) used to
  // leak: the router replaces this whole subtree, so `closeMenu` never ran and the
  // Escape listener stayed behind holding on to detached nodes. Release the globals
  // on navigation instead — same contract the play bar and the phrase preview
  // already follow.
  window.addEventListener('hashchange', releaseGlobals, { once: true });

  // Navigation links: let the hash router take over, just close the drawer.
  menu.querySelectorAll('[data-nav]').forEach((link) => {
    link.addEventListener('click', closeMenu);
  });

  // Logout — hand the user's own data over to the account (upload, then clear the
  // three local stores), wipe the session and return to sign-in. The next sign-in
  // fills those stores again from the account.
  //
  // The handover has to happen **here**, before `clearSession()`: the access token
  // is what tells the server whose backup this is. It is also the last moment we
  // get — nothing else pushes a backup, so signing out is what commits the
  // favorites, dictionary and completions of this session to the account.
  //
  // Best-effort by design: a network failure, a 413 or a 500 must not strand
  // somebody in an account they asked to leave. Nothing is lost when it fails
  // either — `offloadUserData` clears **only** after the upload resolved, so the
  // rows stay on the device and the next sign-out tries again. The button goes busy
  // for the round trip so a second tap can't fire a second upload.
  const logout = mount.querySelector('#menu-logout');
  logout.addEventListener('click', async () => {
    setLoading(logout, true, 'Zapisywanie…');
    try {
      await offloadUserData(getAccessToken());
    } catch {
      /* backup didn't land — sign out anyway, the data is untouched and still here */
    }
    setLoading(logout, false);
    closeMenu();
    clearSession();
    navigate('/login');
  });

  // Delete account — confirm, call the contract endpoint, then erase the device
  // (session *and* the whole local cache) and return to sign-in.
  mount.querySelector('#menu-delete').addEventListener('click', async () => {
    const confirmed = await confirmDialog({
      title: 'Usunąć konto?',
      message:
        'Tej operacji nie można cofnąć. Twoje konto zostanie trwale usunięte, a wraz z nim ' +
        'wszystkie dane zapisane na tym urządzeniu: pobrane tematy i nagrania, ulubione, ' +
        'słownik oraz postęp nauki.',
      confirmLabel: 'Usuń',
      cancelLabel: 'Anuluj',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await deleteAccount(getAccessToken());
    } catch (error) {
      await confirmDialog({
        title: 'Nie udało się usunąć konta',
        message: error?.message || 'Spróbuj ponownie później.',
        confirmLabel: 'OK',
        cancelLabel: 'Zamknij',
      });
      return;
    }

    closeMenu();
    await clearAccountData();
    navigate('/login');
  });
}
